import { Action, createReducer, on } from '@ngrx/store';
import { cloneDeep, merge } from 'lodash-es';
import * as OrderformActions from './orderform.actions';
import {
  OrderformConfig,
  OrderformGlobalSettings,
  OrderformOrderOrderState,
  OrderformOrderState,
  OrderformSettingsState,
  OrderformState,
} from './orderform.models';
import { objectDifference } from '../misc/object-difference';
import { mapFailureCodesToErrorsByPostname } from '../misc/validation-code-mapper';

export const ORDERFORM_FEATURE_KEY = 'orderform';

export interface OrderformPartialState {
  readonly [ORDERFORM_FEATURE_KEY]: OrderformState;
}

export const initialState: OrderformState = {
  order: {
    order: {} as OrderformOrderOrderState,
  } as OrderformOrderState,
  paymentUrl: '',
  pending: false,
  submit: 'initial',
  isExpressCheckoutClicked: false,
};

const orderformReducer = createReducer(
  initialState,
  on(OrderformActions.updateOrderformRequest, (state, { data }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        execute: {
          ...state.order.execute,
          action: 'select',
        },
        requestedOrder: cloneDeep(state.order.order),
      },
      pending: true,
    };
  }),
  on(OrderformActions.validateOrderformRequest, (state, { data }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        execute: {
          ...state.order.execute,
          action: 'check',
        },
      },
      pending: true,
    };
  }),
  on(OrderformActions.sendOrderFormRequest, (state, { data }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        execute: {
          ...state.order.execute,
          action: 'submit',
        },
      },
      paymentUrl: '',
      pending: true,
      submit: 'pending',
    };
  }),
  on(OrderformActions.sendOrderFormRequestValidationError, (state, { data }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        ...data,
      },
      pending: false,
      submit: 'failed',
    };
  }),
  on(OrderformActions.sendOrderFormRequestPollingValidationFailed, (state, { response }) => {
    // Map the failure codes from the order-status API to field-level errors
    const errorsByPostname = response.failure_codes
      ? mapFailureCodesToErrorsByPostname(response.failure_codes, response.failure_reasons)
      : {};

    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        validation: {
          ...state.order.validation,
          errors_by_postname: {
            ...(state.order.validation?.errors_by_postname || {}),
            ...errorsByPostname,
          },
        },
      },
      pending: false,
      submit: 'failed',
    };
  }),
  on(OrderformActions.sendOrderFormRequestPollingFieldValidationFailed, (state, { response }) => {
    // Map the failure codes from the order-status API to field-level errors
    const errorsByPostname = response.failure_codes
      ? mapFailureCodesToErrorsByPostname(response.failure_codes, response.failure_reasons)
      : {};

    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        validation: {
          ...state.order.validation,
          errors_by_postname: {
            ...(state.order.validation?.errors_by_postname || {}),
            ...errorsByPostname,
          },
        },
      },
      pending: false,
      submit: 'failed',
    };
  }),
  on(
    OrderformActions.sendOrderFormRequestError,
    OrderformActions.sendOrderFormResponseShowMessage,
    OrderformActions.formValidationFailedAfterSubmit,
    (state) => {
      return <OrderformState>{
        ...state,
        submit: 'failed',
      };
    }
  ),
  on(OrderformActions.sendOrderFormRequestPaymentUrlResponse, (state, { url }) => {
    return <OrderformState>{
      ...state,
      pending: false,
      submit: 'success',
      paymentUrl: url,
    };
  }),
  on(OrderformActions.sendOrderFormRequestPolling, (state, { orderId }) => {
    return <OrderformState>{
      ...state,
      pending: true,
    };
  }),
  on(OrderformActions.sendOrderFormRequestPollingAfterClientAction, (state, { orderId }) => {
    return <OrderformState>{
      ...state,
      pending: true,
    };
  }),
  on(OrderformActions.sendOrderFormRequestRedirect, (state) => {
    return <OrderformState>{
      ...state,
      pending: false,
      submit: 'success',
    };
  }),
  on(OrderformActions.sendOrderFormRequestPaymentPending, (state, { purchaseId }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        order: {
          ...state.order.order,
          purchase_id: purchaseId,
        },
      },
      pending: false,
      submit: 'initial',
    };
  }),
  on(OrderformActions.updateOrderformSuccess, (state, { data }) => {
    // Safely access is_plain_mode_enabled with fallback to undefined if the path doesn't exist
    const previousSettings = (state.order?.settings ?? {}) as Partial<OrderformSettingsState>;
    const previousGlobalSettings = (previousSettings.global ?? {}) as Partial<OrderformGlobalSettings>;
    const previousConfigSettings = (previousSettings.config ?? {}) as Partial<OrderformConfig>;
    const isPlainModeEnabled = previousGlobalSettings?.is_plain_mode_enabled;
    const previousStrikethrough = previousConfigSettings?.is_strikethrough_active;
    const previousSecuritySealIcons = previousConfigSettings?.new_security_seal_icons;

    const incomingSettings = (data.settings ?? {}) as Partial<OrderformSettingsState>;
    const incomingGlobalSettings = (incomingSettings.global ?? {}) as Partial<OrderformGlobalSettings>;
    const incomingConfigSettings = (incomingSettings.config ?? {}) as Partial<OrderformConfig>;

    const mergedSettings = <OrderformSettingsState>{
      ...previousSettings,
      ...incomingSettings,
      global: <OrderformGlobalSettings>{
        ...previousGlobalSettings,
        ...incomingGlobalSettings,
        is_plain_mode_enabled: isPlainModeEnabled,
      },
      config: <OrderformConfig>{
        ...previousConfigSettings,
        ...incomingConfigSettings,
        ...(typeof previousStrikethrough === 'boolean' ? { is_strikethrough_active: previousStrikethrough } : {}),
        ...(typeof previousSecuritySealIcons === 'boolean'
          ? { new_security_seal_icons: previousSecuritySealIcons }
          : {}),
      },
    };

    const modifiedData = {
      ...data,
      settings: mergedSettings,
    };

    const newState = <OrderformState>{
      ...state,
      order: {
        ...state.order,
        ...modifiedData,
        order: merge(cloneDeep(data.order), objectDifference(state.order.order, state.order.requestedOrder)),
        requestedOrder: {},
      },
      pending: false,
    };

    return newState;
  }),
  on(OrderformActions.validateOrderformRequestSuccess, (state, { data }) => {
    const previousSettings = (state.order?.settings ?? {}) as Partial<OrderformSettingsState>;
    const previousGlobalSettings = (previousSettings.global ?? {}) as Partial<OrderformGlobalSettings>;
    const previousConfigSettings = (previousSettings.config ?? {}) as Partial<OrderformConfig>;
    const isPlainModeEnabled = previousGlobalSettings?.is_plain_mode_enabled;
    const previousStrikethrough = previousConfigSettings?.is_strikethrough_active;
    const previousSecuritySealIcons = previousConfigSettings?.new_security_seal_icons;
    const incomingSettings = (data.settings ?? {}) as Partial<OrderformSettingsState>;
    const incomingGlobalSettings = (incomingSettings.global ?? {}) as Partial<OrderformGlobalSettings>;
    const incomingConfigSettings = (incomingSettings.config ?? {}) as Partial<OrderformConfig>;

    const mergedSettings = <OrderformSettingsState>{
      ...previousSettings,
      ...incomingSettings,
      global: <OrderformGlobalSettings>{
        ...previousGlobalSettings,
        ...incomingGlobalSettings,
        is_plain_mode_enabled: isPlainModeEnabled,
      },
      config: <OrderformConfig>{
        ...previousConfigSettings,
        ...incomingConfigSettings,
        ...(typeof previousStrikethrough === 'boolean' ? { is_strikethrough_active: previousStrikethrough } : {}),
        ...(typeof previousSecuritySealIcons === 'boolean'
          ? { new_security_seal_icons: previousSecuritySealIcons }
          : {}),
      },
    };

    const modifiedData = {
      ...data,
      settings: mergedSettings,
    };

    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        ...modifiedData,
      },
      pending: false,
    };
  }),
  on(
    OrderformActions.validateOrderformRequestError,
    OrderformActions.updateOrderformError,
    OrderformActions.sendOrderFormRequestError,
    OrderformActions.beforeSubmitOrderformFailed,
    OrderformActions.beforeSubmitOrderformSuccess,
    OrderformActions.afterSubmitOrderFormFailed,
    OrderformActions.sendOrderFormResponseShowMessage,
    (state, {}) => {
      return <OrderformState>{
        ...state,
        pending: false,
      };
    }
  ),

  on(OrderformActions.setToOrderStore, (state, { update }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        order: {
          ...state.order.order,
          ...cloneDeep(update),
        },
      },
    };
  }),

  on(OrderformActions.updateToOrderformGlobalSettings, (state, { update }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        settings: {
          ...state.order.settings,
          global: {
            ...state.order.settings.global,
            ...update,
          },
        },
      },
    };
  }),

  on(OrderformActions.updateConfigProductId, (state, { productId }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        settings: {
          ...state.order.settings,
          config: {
            ...state.order.settings.config,
            product_id: productId,
          },
        },
      },
    };
  }),

  on(OrderformActions.updateProductQuantity, (state, { product_id, quantity }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        order: {
          ...state.order.order,
          items: {
            ...state.order.order.items,
            [product_id]: {
              ...state.order.order.items[product_id],
              quantity: {
                ...state.order.order.items[product_id].quantity,
                value: quantity,
              },
            },
          },
        },
      },
    };
  }),

  on(OrderformActions.updateProductVariantId, (state, { product_id, variant_id }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        order: {
          ...state.order.order,
          items: {
            ...state.order.order.items,
            [product_id]: {
              ...state.order.order.items[product_id],
              variant_id,
            },
          },
        },
      },
    };
  }),

  on(OrderformActions.updateProductETicketId, (state, { product_id, eticket_date_id }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        order: {
          ...state.order.order,
          items: {
            ...state.order.order.items,
            [product_id]: {
              ...state.order.order.items[product_id],
              eticket_date_id,
            },
          },
        },
      },
    };
  }),

  on(OrderformActions.updateProductCustomForm, (state, { product_id, forms }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        order: {
          ...state.order.order,
          items: {
            ...state.order.order.items,
            [product_id]: {
              ...state.order.order.items[product_id],
              customForm: forms,
            },
          },
        },
      },
    };
  }),

  on(OrderformActions.checkDoubleOrderResponse, (state, { data }) => {
    if (data.type === 'hidden') {
      return state;
    }
    return {
      ...state,
      order: {
        ...state.order,
        messages: {
          ...state.order.messages,
          notifications: [data],
        },
      },
    };
  }),

  on(OrderformActions.afterMessageShown, (state, { messageType, message }) => {
    const list = state.order.messages[messageType].slice(0);
    const index = list.indexOf(message);
    if (index < 0) {
      return state;
    }
    list.splice(index, 1);
    return {
      ...state,
      order: {
        ...state.order,
        messages: {
          ...state.order.messages,
          [messageType]: list,
        },
      },
    };
  }),

  on(OrderformActions.removeValidationError, (state, { field }) => {
    const errors_by_postname = cloneDeep(state.order.validation.errors_by_postname);
    delete errors_by_postname[field];
    return {
      ...state,
      order: {
        ...state.order,
        validation: {
          ...state.order.validation,
          errors_by_postname,
        },
      },
    };
  }),

  on(OrderformActions.expressCheckoutButtonClicked, (state) => ({
    ...state,
    isExpressCheckoutClicked: true,
  })),

  on(OrderformActions.normalCheckoutButtonClicked, (state) => ({
    ...state,
    isExpressCheckoutClicked: false,
  })),

  on(
    OrderformActions.formValidationSuccess,
    OrderformActions.formValidationFailedBeforeSubmit,
    OrderformActions.sendOrderFormRequestError,
    (state) => ({
      ...state,
      isExpressCheckoutClicked: false,
    })
  ),

  /**
   * LocalStorage Progress Actions
   */
  on(OrderformActions.loadProgressFromLocalStorage, (state, { data }) => {
    return <OrderformState>{
      ...state,
      order: {
        ...state.order,
        order: {
          ...state.order.order,
          shipping_address: {
            ...state.order.order.shipping_address,
            ...data.shipping_address,
          },
          billing_address: {
            ...state.order.order.billing_address,
            ...data.billing_address,
          },
          has_billing_address: data.has_billing_address ?? state.order.order.has_billing_address,
        },
      },
    };
  })
);

export function reducer(state: OrderformState | undefined, action: Action) {
  return orderformReducer(state, action);
}
